import { create } from 'zustand';

import {
  classifyVisibleRange,
  createWindowPreference,
  DEFAULT_VISIBLE_SECONDS,
  normalizeWindowPreference,
  sameWindowPreference,
  WINDOW_MODE,
} from '../Utils/chartTimeWindow';
import { etDateKey } from '../Utils/marketHours';

const STORAGE_KEY = 'spx_chart_time_window_v1';

function loadPreference() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return normalizeWindowPreference(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writePreference(preference) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (!preference) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
  } catch {
    // quota exceeded or storage unavailable -- degrade to non-persisted
  }
}

// A pan publishes on every frame, and localStorage.setItem is synchronous. The
// first write lands immediately so a single action persists at once; writes that
// follow inside the window are coalesced into one trailing flush.
const PERSIST_COALESCE_MS = 250;
let persistTimer = null;
let pendingPreference;

function flushPreference() {
  persistTimer = null;
  if (pendingPreference === undefined) return;
  const preference = pendingPreference;
  pendingPreference = undefined;
  writePreference(preference);
}

function savePreference(preference) {
  if (persistTimer) {
    pendingPreference = preference;
    return;
  }
  writePreference(preference);
  persistTimer = setTimeout(flushPreference, PERSIST_COALESCE_MS);
}

// Exposed so a caller that must observe storage right away (tests, teardown)
// does not have to wait out the window.
export function flushChartTimeWindowPersist() {
  if (persistTimer) clearTimeout(persistTimer);
  flushPreference();
}

export const useChartTimeWindowStore = create((set, get) => ({
  preference: loadPreference(),
  // Identifies the chart that produced the current preference so a target can
  // skip re-applying its own published intent.
  source: null,
  replayWindowActive: false,
  preReplayPreference: null,
  replayTradingDate: null,
  replayPreferencesByDate: {},

  /**
   * Publish a user-driven visible range. Programmatic range changes must never
   * reach this: pass only ranges observed while a user-intent window is open.
   */
  publishUserRange: (source, range, extent, options = {}) => {
    // `createWindowPreference` normalizes and validates forced absolute ranges
    // after this classification bypass, matching the normal path's contract.
    const intent = options.forceAbsolute
      ? { mode: WINDOW_MODE.ABSOLUTE, from: range?.from, to: range?.to }
      : classifyVisibleRange(range, extent, options);
    if (!intent) return false;
    const preference = createWindowPreference(intent, options);
    if (!preference) return false;

    const current = get().preference;
    // A no-op republish would still bump `updatedAt` and wake every subscriber
    // for a range they already show.
    if (sameWindowPreference(current, preference) && get().source === source) return false;

    const state = get();
    if (state.replayWindowActive && state.replayTradingDate) {
      set({
        preference,
        source,
        replayPreferencesByDate: {
          ...state.replayPreferencesByDate,
          [state.replayTradingDate]: preference,
        },
      });
    } else {
      set({ preference, source });
      savePreference(preference);
    }
    return true;
  },

  followReplayCutoff: (cutoffMs) => {
    const state = get();
    const replayTradingDate = etDateKey(cutoffMs);
    if (!state.replayWindowActive || state.replayTradingDate !== replayTradingDate) {
      const replayPreferencesByDate = { ...state.replayPreferencesByDate };
      if (state.replayWindowActive && state.replayTradingDate && state.preference) {
        replayPreferencesByDate[state.replayTradingDate] = state.preference;
      }
      const preference = replayPreferencesByDate[replayTradingDate]
        || createWindowPreference(
          { mode: WINDOW_MODE.SESSION },
          { now: cutoffMs, tradingDate: replayTradingDate },
        );
      set({
        replayWindowActive: true,
        preReplayPreference: state.replayWindowActive
          ? state.preReplayPreference
          : state.preference,
        replayTradingDate,
        replayPreferencesByDate,
        preference,
        source: 'replay',
      });
      return true;
    }

    const current = state.preference;
    if (current?.mode === WINDOW_MODE.SESSION) return false;
    // A pinned position is one somebody panned to. Advancing the playhead must
    // not drag it back, and the rolling rebuild below would also replace its
    // span with the three-hour default. A Custom window that came from a seek
    // carries no pin and still resets.
    if (current?.mode === WINDOW_MODE.ABSOLUTE && current.positionPinned) return false;

    const durationSeconds = current?.mode === WINDOW_MODE.LATEST
      ? current.durationSeconds
      : DEFAULT_VISIBLE_SECONDS;
    const isDefaultSpan = current?.mode === WINDOW_MODE.LATEST
      ? current.isDefaultSpan
      : true;
    const preference = createWindowPreference(
      { mode: WINDOW_MODE.LATEST, durationSeconds, isDefaultSpan },
      { now: cutoffMs },
    );
    if (!preference) return false;
    if (sameWindowPreference(current, preference) && get().source === 'replay') return false;

    set({
      preference,
      source: 'replay',
      replayPreferencesByDate: {
        ...state.replayPreferencesByDate,
        [replayTradingDate]: preference,
      },
    });
    return true;
  },

  endReplayWindow: (liveTradingDate) => {
    const state = get();
    if (!state.replayWindowActive) return false;
    const preReplayPreference = normalizeWindowPreference(state.preReplayPreference);
    const preferenceIsValid = preReplayPreference
      && (
        preReplayPreference.mode !== WINDOW_MODE.ABSOLUTE
        || preReplayPreference.tradingDate === liveTradingDate
      );
    const preference = preferenceIsValid
      ? preReplayPreference
      : createWindowPreference(
        { mode: WINDOW_MODE.SESSION },
        { tradingDate: liveTradingDate },
      );
    const replayPreferencesByDate = { ...state.replayPreferencesByDate };
    if (state.replayTradingDate && state.preference) {
      replayPreferencesByDate[state.replayTradingDate] = state.preference;
    }
    set({
      preference,
      source: 'replay-exit',
      replayWindowActive: false,
      preReplayPreference: null,
      replayTradingDate: null,
      replayPreferencesByDate,
    });
    if (!preferenceIsValid) savePreference(preference);
    return true;
  },

  // Deliberately no `clearPreference`. Every consumer early-returns on a null
  // preference, so clearing resets only the chart that cleared it and strands
  // the others on whatever they were showing. A reset is expressed by
  // publishing the default rolling range, which propagates.
}));

export const CHART_TIME_WINDOW_STORAGE_KEY = STORAGE_KEY;
